import { Controller, useForm } from "react-hook-form"
import { useVectorModal } from "../../../hooks/useVectorMemoryModal"
import { zodResolver } from "@hookform/resolvers/zod"
import z from "zod"

import {
	Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    Tooltip,
    type DialogProps
} from "@mui/material"
import { FileText, X } from "@phosphor-icons/react"

import { BULK_MAX_DISPLAY_ITEMS, selectChunkingOptions } from "."

import type { VectorChunkingMethod } from "../../../types/vector"

const chunkingMethodSchema = z.object({
	chunkingMethod: z.string().min(1, { message: 'Chunking method is required' })
})

type ChunkingMethodData = z.infer<typeof chunkingMethodSchema>

interface VectorChunkingBulkEditModalProps extends DialogProps {
	onOpenChange: (open?: boolean) => void
}

function VectorChunkingBulkEditModal({
	open,
	onOpenChange,
	...props
}: VectorChunkingBulkEditModalProps) {
	const {
		handleSubmit,
		control,
		formState: { errors },
		reset
	} = useForm({
		defaultValues: {
			chunkingMethod: ''
		},
		resolver: zodResolver(chunkingMethodSchema)
	})

	const { selectedStagingFiles, stagingFiles, onStagingFilesChange } = useVectorModal()

	const handleChunkingMethodBulkChange = ({ chunkingMethod }: ChunkingMethodData) => {
		const newFileList = stagingFiles.map((file) => {
			if (selectedStagingFiles.includes(file.id)) {
				return {
					...file,
					chunking: chunkingMethod as VectorChunkingMethod,
				}
			}

			return file
		})

		onStagingFilesChange(newFileList)
		onOpenChange(false)

		reset()
	}

	const allFiles = stagingFiles.filter(({ id }) => selectedStagingFiles.includes(id))
	const visibleFiles = allFiles.length > BULK_MAX_DISPLAY_ITEMS ? allFiles.slice(0, BULK_MAX_DISPLAY_ITEMS) : allFiles
	const hiddenFiles = visibleFiles.length < allFiles.length ? allFiles.slice(BULK_MAX_DISPLAY_ITEMS) : null

	return (
		<Dialog
			open={open}
			fullWidth
			sx={{
				'.MuiPaper-root': {
					minWidth: '1000px',
					maxWidth: '1000px',
					borderRadius: '1rem',

					'&>*+*': {
						marginBlockStart: '1.5rem',
					}
				},
			}}
			{...props}
		>
			<header style={{ paddingBlockStart: '2rem', paddingInline: '2rem' }}>
				<DialogTitle sx={{ paddingInline: 0 }} fontSize="1.25rem">
					Manage Chunking for selected files
				</DialogTitle>

				<List 
					sx={{
						paddingInline: 0,
						display: 'flex',
						flexWrap: 'wrap',
						gap: '1rem'
					}}
				>
					{visibleFiles.map(({ id, fileName }) => (
						<ListItem
							key={id}
							sx={{
								padding: '0.5rem',

								width: 'auto',
								height: '100%',

								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',

								backgroundColor: '#fafafa',

								borderStyle: 'solid',
								borderWidth: '1px',
								borderColor: '#d4d4d4',
								borderRadius: '0.5rem',
							}}
						>
							<FileText size={24} />
							{fileName}
						</ListItem>
					))}

					{hiddenFiles ? (
						<Tooltip
							title={
								<>
									{hiddenFiles.map(({ id, fileName }) => (
										<p key={id}>{fileName}</p>
									))}
								</>
							}
						>
							<p
								style={{
									display: 'flex',
									alignItems: 'center',
									margin: 0
								}}
							>
								+ {hiddenFiles.length} more
							</p>
						</Tooltip>
					) : null}
				</List>
			</header>
			
			<div style={{ paddingInline: '2rem' }}>
				<Divider />
			</div>

			<DialogContent
				sx={{
					paddingBlock: 0,
					paddingInline: '2rem',

					overflow: 'visible'
				}}
			>
				<Box
					id="chunking-method-bulk-form"
					component="form"
					onSubmit={handleSubmit(handleChunkingMethodBulkChange)}
				>
					<Controller
						control={control}
						name="chunkingMethod"
						render={({ field }) => (
							<>
								<FormControl 
									fullWidth
									variant="outlined"
									size="medium"
									error={!!errors.chunkingMethod}
								>
									<InputLabel 
										id="bulk-chunking-select"
										sx={{ color: "#9e9e9e" }}
									>
										Choose a chunking method
									</InputLabel>

									<Select
										id="bulk-chunking-select"
										fullWidth
										sx={{
											marginBottom: 0,
										}}
										label="Choose a chunking method"
										labelId="chunking"
										value={field.value}
										onChange={(evt) =>
											field.onChange(evt.target.value as VectorChunkingMethod)
										}
									>
										{selectChunkingOptions.map(({ label, value }) => (
											<MenuItem key={value} value={value}>
												Chunk by {label}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<FormHelperText>{errors.chunkingMethod?.message}</FormHelperText>
							</>
						)}
					/>
				</Box>
			</DialogContent>

			<DialogActions
				sx={{
					borderBlockStartWidth: '1px',
					borderBlockStartStyle: 'solid',
					borderBlockStartColor: '#EAECF0',

					paddingInline: '2rem',
					paddingBlock: '1rem'
				}}
			>
				<Button
					type="submit"
					variant="contained"
					form="chunking-method-bulk-form"
				>
					Save Changes
				</Button>
			</DialogActions>

			<Button
				sx={{
					position: 'absolute',
					right: '1.25rem',
					top: '1.25rem',
					padding: '0.25rem',
					minWidth: 'auto',
					zIndex: '10',
					margin: '0 !important',
				}}
				onClick={() => onOpenChange(false)}
			>
				<span style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					padding: 0,
					margin: '-1px',
					overflow: 'hidden',
					clip: 'rect(0, 0, 0, 0)',
					whiteSpace: 'nowrap',
					borderWidth: 0
				}}>
					Close
				</span>

				<X width={24} height={24} />
			</Button>
		</Dialog>
	)
}

export { VectorChunkingBulkEditModal }